import { ENGLISH_SHORT_DATE_FORMAT } from '../../../../date-manager/date-format-utility-methods/locale-settings/english-date-locale-settings';
import { JAPANESE_SHORT_DATE_FORMAT } from '../../../../date-manager/date-format-utility-methods/locale-settings/japanese-date-locale-settings';
import { DomainPreferences } from '../../../api/types';
import { RegularMetricId } from '../../../api/types-graphql';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { KpiFactboxTwoMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-two-metric/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../common/components/storyboard-components/common/types';
import { Months } from '../../../common/components/timeslider/types';
import { Languages } from '../../../constants';
import {
  addYear,
  fiscalYearStart,
  format,
  getMomentDate,
} from '../../../date/date-utils';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  SingleValueTimeSelection,
  TimeSelectionType,
} from '../../../types';
import { StoryboardDateUtils } from '../utils';

/**
 * Month-end timestamps for each month in a fiscal year, relative to the storyboard
 * reference date (typically the end of the FY selected on the time slider).
 *
 * @param fiscalYearOffset - 0 = FY containing `now`, -1 = previous FY, -2 = two FYs prior, etc.
 */
export const getFiscalYearMonths = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences,
  fiscalYearOffset = 0
): number[] => {
  const { now } = storyboardDateUtils;
  const finYearStartMonth = domainPreferences.finYearStartMonth ?? Months.April;
  let fyStart = fiscalYearStart(now, finYearStartMonth);
  fyStart = addYear(fyStart, fiscalYearOffset);

  const months: number[] = [];
  for (let i = 0; i < 12; i++) {
    months.push(
      getMomentDate(fyStart).add(i, 'month').endOf('month').valueOf()
    );
  }

  return months;
};

export const getFiscalYearTimeSelection = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences,
  fiscalYearOffset = 0
): CalendarYearSingleValueByMonthsTimeSelection => {
  const months = getFiscalYearMonths(
    storyboardDateUtils,
    domainPreferences,
    fiscalYearOffset
  );
  const start = format(getMomentDate(months[0]).startOf('month').valueOf());
  const end = format(months[months.length - 1]);
  return {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start,
      end,
    },
  };
};

/**
 * Generates a SQL filter to exclude records where Custom Field 7 Level 2
 * equals the most recently completed fiscal year.
 *
 * Fiscal Year Definition: April 1st to March 31st
 * Format: YYYY年度 (e.g., '2024年度')
 *
 * Example:
 * - If today is 2025/04/01, the most recently completed fiscal year is 2024
 *   (April 2024 - March 2025), so filter excludes '2024年度'
 */
export const getPreviousFiscalYearExclusionFilter = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): string => {
  const { fiscalYear } = storyboardDateUtils;
  const lastFiscalYear = fiscalYear(domainPreferences) - 1;

  // Format as YYYY年度
  const fiscalYearString = `${lastFiscalYear}年度`;

  return `EMPLOYEE.CUSTOM_FIELD_7_LEVEL_2 != '${fiscalYearString}'`;
};

export const getEligibleForParentalLeaveFactbox = (
  timeSelection: SingleValueTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '1',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '出産した従業員数',
          [Languages.JA]: '出産した従業員数',
        },
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: '女性',
              [Languages.JA]: '女性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0171EligibleForParentalLeaveCount,
              },
              filters: `EMPLOYEE.GENDER = 'Female'`,
              timeSelection,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: '男性',
              [Languages.JA]: '男性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0171EligibleForParentalLeaveCount,
              },
              filters: `EMPLOYEE.GENDER = 'Male'`,
              timeSelection,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
        },
        footer: {
          [Languages.EN]: '※男性の場合は配偶者が出産',
          [Languages.JA]: '※男性の場合は配偶者が出産',
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
  };
};

export const getParentalLeaveTakenFactbox = (
  timeSelection: SingleValueTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '2',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '育児休業等を取得した従業員数',
          [Languages.JA]: '育児休業等を取得した従業員数',
        },
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: '女性',
              [Languages.JA]: '女性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0171ParentalLeaveTakenCount,
              },
              filters: `EMPLOYEE.GENDER = 'Female'`,
              timeSelection,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: '男性',
              [Languages.JA]: '男性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0171ParentalLeaveTakenCount,
              },
              filters: `EMPLOYEE.GENDER = 'Male'`,
              timeSelection,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
  };
};

export const getParentalLeaveTakenUtilizationFactbox = (
  timeSelection: SingleValueTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '1',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '育児休業等を取得した割合',
          [Languages.JA]: '育児休業等を取得した割合',
        },
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: '女性',
              [Languages.JA]: '女性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0171ParentalLeaveTakenUtilizationPerc,
              },
              filters: `EMPLOYEE.GENDER = 'Female'`,
              timeSelection,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: '男性',
              [Languages.JA]: '男性',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0171ParentalLeaveTakenUtilizationPerc,
              },
              filters: `EMPLOYEE.GENDER = 'Male'`,
              timeSelection,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
              },
            ],
          },
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
  };
};

export const getIndividualsCountFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const customFilter8 = `EMPLOYEE.CUSTOM_FILTER_8 IN ('【出産】妻産休暇', '【育児】子の看護等休暇', '【育児】子の看護欠勤')`;
  const customField6Level2Filter = `EMPLOYEE.CUSTOM_FIELD_6_LEVEL_2 IN ('1', '2', '3', '4', '5')`;
  const maleGenderFilter = `EMPLOYEE.GENDER = 'Male'`;
  return {
    chartId: '4',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '育児目的休暇制度利用男性従業員数',
          [Languages.JA]: '育児目的休暇制度利用男性従業員数',
        },
        footer: {
          [Languages.EN]: '※小学校就学の始期に達するまでの子がいる従業員',
          [Languages.JA]: '※小学校就学の始期に達するまでの子がいる従業員',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_2IndividualsActiveInPeriodCount,
            },
            timeSelection: getFiscalYearTimeSelection(
              storyboardDateUtils,
              domainPreferences
            ),
            filters: `${customFilter8} 
            AND 
            ${customField6Level2Filter} 
            AND 
            ${maleGenderFilter}
            AND
            ${getPreviousFiscalYearExclusionFilter(storyboardDateUtils, domainPreferences)}
            `,
          },
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getMonthlyOvertimeFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): StoryboardItemConfig => {
  const months = getFiscalYearMonths(storyboardDateUtils, domainPreferences);

  const employmentTypeFilter = `EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = '正規雇用（期間の定めなく雇用する者）'`;
  const fullTimeCustomFilter1 = `EMPLOYEE.CUSTOM_FILTER_1 = 'フルタイム'`;
  const bottom1OvertimeFilters = `${employmentTypeFilter} AND ${fullTimeCustomFilter1}`;

  // Create subQueries for each of the 12 months with formatted titles
  const subQueries = months.map((monthTimestamp) => {
    const formattedDate = format(monthTimestamp);
    const formattedTitleEn = getMomentDate(monthTimestamp).format(
      ENGLISH_SHORT_DATE_FORMAT
    );
    const formattedTitleJa = getMomentDate(monthTimestamp).format(
      JAPANESE_SHORT_DATE_FORMAT
    );

    const timeSelection: SingleValueTimeSelection = {
      type: TimeSelectionType.CalendarYearSingleValueByMonths,
      input: {
        start: formattedDate,
        end: formattedDate,
      },
    };

    return {
      title: {
        [Languages.EN]: formattedTitleEn,
        [Languages.JA]: formattedTitleJa,
      },
      query: {
        metric: {
          type: 'RegularMetricId' as const,
          value: RegularMetricId.Pm0470_1OvertimeHolidayHoursAvgAllEmp,
        },
        timeSelection,
        filters: bottom1OvertimeFilters,
      },
    };
  });

  return {
    chartId: '5',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]:
            'フルタイムの労働者1人あたりの各月ごとの時間外労働及び休日労働の合計時間数',
          [Languages.JA]:
            'フルタイムの労働者1人あたりの各月ごとの時間外労働及び休日労働の合計時間数',
        },
        section: {
          subQueries,
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getIndividualsCount2Factbox = (
  timeSelection: SingleValueTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '6',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]:
            '1ヶ月の時間外労働時間(法定外)平均が60時間以上の従業員数',
          [Languages.JA]:
            '1ヶ月の時間外労働時間(法定外)平均が60時間以上の従業員数',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_2IndividualsActiveInPeriodCount,
            },
            timeSelection,
            filters: `EMPLOYEE.CUSTOM_FIELD_8_LEVEL_1 = '60時間以上'`,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getEligibleForParentalLeave2Factbox = (
  timeSelection: SingleValueTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '7',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '前々事業年度に出産した女性従業員数',
          [Languages.JA]: '前々事業年度に出産した女性従業員数',
        },
        footer: {
          [Languages.EN]: '※出産の日において在籍している女性労働者に限る',
          [Languages.JA]: '※出産の日において在籍している女性労働者に限る',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0171EligibleForParentalLeaveCount,
            },
            timeSelection,
            filters: `EMPLOYEE.GENDER = 'Female'`,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getEligibleForParentalLeave3Factbox = (
  timeSelection: SingleValueTimeSelection
): StoryboardItemConfig => {
  return {
    chartId: '8',
    layoutConfig: {
      gridColSpan: 5,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: '出産以降も在籍している女性従業員数',
          [Languages.JA]: '出産以降も在籍している女性従業員数',
        },
        footer: {
          [Languages.EN]: '※育休業等又は育児目的休暇制度利用者も含む',
          [Languages.JA]: '※育休業等又は育児目的休暇制度利用者も含む',
        },
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0171EligibleForParentalLeaveCount,
            },
            timeSelection,
            filters: `EMPLOYEE.GENDER = 'Female' AND EMPLOYEE.EMPLOYMENT_TEMPORALITY = 'PRESENT' AND EMPLOYEE.DAYS_IN_COMPANY >= 365`,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};
